import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { extractErrorMessage } from '@core/http/api-response.envelope';
import { ToastService } from '@app/shared/components/toast/services/toast.service';

import { AuthService } from '../../../services/auth.service';
import { TokenStorageService } from '../../../services/token-storage.service';
import {
  submitUsername,
  submitUsernameSuccess,
  submitUsernameFailure,
  verifyOtp,
  verifyOtpSuccess,
  verifyOtpFailure,
  resendOtp,
  resendOtpSuccess,
  resendOtpFailure,
  setPassword,
  setPasswordSuccess,
  setPasswordFailure,
  setError,
  reset,
} from './account-activation.actions';
import { googleAuthStepEntered } from '../../login/store/login.actions';
import {
  selectActivationToken,
  selectUsername,
} from './account-activation.selectors';

@Injectable()
export class AccountActivationEffects {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  submitUsername$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitUsername),
      switchMap(({ username }) =>
        this.authService.checkUsername(username).pipe(
          map(res => {
            const data = res?.data ?? ({} as Record<string, never>);
            return submitUsernameSuccess({
              username,
              activationToken: data.token ?? null,
              tokenType: data.tokenType ?? null,
              nextStep: data.nextStep ?? null,
              loginMethod: data.loginMethod ?? null,
              expiresIn: data.expiresIn ?? null,
              expiresAt: data.expiresAt ?? null,
              message: data.message ?? null,
            });
          }),
          catchError((err: any) =>
            of(submitUsernameFailure({ error: this.normalizeApiError(err) }))
          )
        )
      )
    )
  );

  submitUsernameSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(submitUsernameSuccess),
        map(() => {
          this.router.navigate(['/auth/account-activation/verify-otp']);
        })
      ),
    { dispatch: false }
  );

  verifyOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyOtp),
      withLatestFrom(this.store.select(selectActivationToken)),
      switchMap(([{ code }, activationToken]) => {
        if (!activationToken) {
          return of(
            setError({
              error: this.translate.instant(
                'auth.activation.errors.missingActivationCode'
              ),
            })
          );
        }
        return this.authService
          .verifyOtp({ otpCode: code, activationToken })
          .pipe(
            map(res => {
              const data = res?.data;
              return verifyOtpSuccess({
                activationToken: data?.token ?? null,
                tokenType: data?.tokenType ?? null,
                nextStep: data?.nextStep ?? null,
                message: data?.message ?? null,
              });
            }),
            catchError((err: any) =>
              of(verifyOtpFailure({ error: this.normalizeApiError(err) }))
            )
          );
      })
    )
  );

  verifyOtpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(verifyOtpSuccess),
        map(() => {
          this.router.navigate(['/auth/account-activation/set-password']);
        })
      ),
    { dispatch: false }
  );

  resendOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resendOtp),
      withLatestFrom(this.store.select(selectUsername)),
      switchMap(([, username]) => {
        if (!username) {
          return of(
            setError({
              error: this.translate.instant(
                'auth.activation.errors.missingUsername'
              ),
            })
          );
        }
        return this.authService.checkUsername(username).pipe(
          map(res => {
            const data = res?.data ?? ({} as Record<string, never>);
            return resendOtpSuccess({
              activationToken: data.token ?? null,
              tokenType: data.tokenType ?? null,
              nextStep: data.nextStep ?? null,
              loginMethod: data.loginMethod ?? null,
              expiresIn: data.expiresIn ?? null,
              expiresAt: data.expiresAt ?? null,
              message: data.message ?? null,
            });
          }),
          catchError((err: any) =>
            of(resendOtpFailure({ error: this.normalizeApiError(err) }))
          )
        );
      })
    )
  );

  setPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setPassword),
      withLatestFrom(this.store.select(selectActivationToken)),
      switchMap(([{ password }, activationToken]) => {
        if (!activationToken) {
          return of(
            setError({
              error: this.translate.instant(
                'auth.activation.errors.expiredSession'
              ),
            })
          );
        }
        return this.authService.setPassword({ activationToken, password }).pipe(
          mergeMap(res => {
            const data = res?.data;
            // The account's login rule requires Google Authenticator: the
            // password is set but no session opens until the new member
            // enrols, on the login flow's G-Auth screen.
            if (data?.nextStep?.toUpperCase() === 'SETUP_2FA') {
              if (!data.token) {
                return [
                  setError({
                    error: this.translate.instant(
                      'auth.activation.errors.generic'
                    ),
                  }),
                ];
              }
              this.markWelcomeModalPending();
              return [
                reset(),
                googleAuthStepEntered({
                  sessionToken: data.token,
                  setupRequired: true,
                }),
              ];
            }
            const accessToken: string | null = data?.accessToken ?? null;
            const refreshToken: string | null = data?.refreshToken ?? null;
            // No session, no success: saving a null pair would erase any
            // session already in this browser (F-038), as login refuses too.
            if (!accessToken || !refreshToken) {
              return [
                setError({
                  error: this.translate.instant(
                    'auth.activation.errors.generic'
                  ),
                }),
              ];
            }
            this.markWelcomeModalPending();
            this.tokenStorage.save(accessToken, refreshToken);
            return [
              setPasswordSuccess({
                accessToken,
                refreshToken,
                nextStep: data?.nextStep ?? null,
              }),
            ];
          }),
          catchError((err: any) =>
            of(setPasswordFailure({ error: this.normalizeApiError(err) }))
          )
        );
      })
    )
  );

  /**
   * The flow's own refusals (guard clauses, a completion with no session)
   * send no request, or one the interceptor toasted as a success, and no
   * activation screen renders `state.error` — so they are toasted here
   * (F-043's activation half).
   */
  refusalToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setError),
        tap(({ error }) => {
          if (error) this.toast.error(error);
        })
      ),
    { dispatch: false }
  );

  setPasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setPasswordSuccess),
        map(() => {
          this.router.navigate(['/dashboard/account-settings']);
        })
      ),
    { dispatch: false }
  );

  private markWelcomeModalPending(): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      sessionStorage.setItem('citex-dashboard-welcome-modal-trigger', 'true');
      sessionStorage.removeItem('citex-dashboard-welcome-modal-dismissed');
    } catch {
      // ignore storage failures
    }
  }

  private normalizeApiError(err: unknown): string {
    return extractErrorMessage(
      err,
      this.translate.instant('auth.activation.errors.generic')
    );
  }
}
